'use strict'

import store from '../../pages/main/store.js'
import { axiosGet, axiosPost, UPLOAD_CONFIG } from '../../axios/axios.js'
import loadImage from 'blueimp-load-image'
import { date } from 'quasar'
import MD5 from 'crypto-js/md5'

export const CERTIFICATE_DOCUMENT_TYPE_OPTIONS = [
  {
    value: 'certificate',
    label: 'Сертификат'
  },
  {
    value: 'testReport',
    label: 'Протоколы испытаний'
  },
  {
    value: 'informationLetter',
    label: 'Информационное письмо'
  },
  {
    value: 'passport',
    label: 'Паспорт'
  },
  {
    value: 'otherDocuments',
    label: 'Другое'
  }
]

export async function getFileOriginalTime (file) {
  // Если это фотография, читаем Exif
  let originalTime = null
  const fileType = file.type
  if (fileType && fileType.startsWith('image/')) {
    await loadImage(file, {
      meta: true
    })
      .then(function (data) {
        if (data.exif) {
          let originalTimeText = null
          // console.log(data.exif.getAll()) // Все теги Exif
          const exifIFD = data.exif.get('Exif')
          if (exifIFD) {
            originalTimeText = exifIFD.get('DateTimeOriginal')
          }
          if (!originalTimeText) {
            originalTimeText = data.exif.get('DateTime')
          }
          if (originalTimeText) {
            originalTime = date.extractDate(originalTimeText, 'YYYY:MM:DD HH:mm:ss').getTime()
          }
        }
      })
      .catch(function (err) {
        console.error(err)
      })
  }
  if (!originalTime) {
    originalTime = file.lastModified
  }
  console.log('Original time: ' + (originalTime ? new Date(originalTime) : originalTime))
  return originalTime
}

export async function getFileOriginalHash (file) {
  let originalHash = null
  const fileType = file.type
  if (fileType && fileType.startsWith('image/')) {
    originalHash = await new Promise(resolve => {
      const reader = new FileReader()
      reader.onload = function (event) {
        const binary = event.target.result
        const hash = MD5(binary).toString()
        resolve(hash)
      }
      reader.readAsBinaryString(file)
    })
    console.log('Original hash: ' + originalHash)
  }
  return originalHash
}

export function isFileAllowed (file, isImage = false, isVideo = false) {
  const fileExtension = getFileExtension(file.name)
  if (isImage) {
    if (!file.type.startsWith('image/') || fileExtension === null || UPLOAD_CONFIG.prohibitedImageExtensions.has(fileExtension)) {
      return {
        result: false,
        message: 'Файл недопустимого формата: ' + file.name + '.\n' +
        'Разрешены форматы: ' + Array.from(UPLOAD_CONFIG.resizableImageExtensions).map(ext => ext.substring(1, ext.length)).join(', ')
      }
    }
  }
  if (isVideo) {
    if (!file.type.startsWith('video/') || fileExtension === null || !UPLOAD_CONFIG.allowedVideoExtensions.has(fileExtension)) {
      return {
        result: false,
        message: 'Файл недопустимого формата:\n' + file.name
      }
    }
  }
  if (file.size > UPLOAD_CONFIG.maxFileSize) {
    return {
      result: false,
      message: `Файл слишком большой: ${(file.size / (1024 * 1024 * 1024)).toFixed(2)}ГБ. Максимально разрешено: ${UPLOAD_CONFIG.maxFileSize / (1024 * 1024 * 1024)}ГБ\n${file.name}`
    }
  }
  return {
    result: true
  }
}

function getFileExtension (fileName) {
  const extensionIndex = fileName.lastIndexOf('.')
  if (extensionIndex !== -1 && extensionIndex !== fileName.length - 1) {
    return fileName.substring(extensionIndex).toLowerCase()
  }
  return null
}

// Служебный параметр _cb для инвалидации кэша браузера
function invalidateFileImageUrl (fileWrapper) {
  if (!fileWrapper || fileWrapper.isToUpload || !fileWrapper.url) {
    return
  }
  const url = fileWrapper.url.replace(/([?&])_cb=\d+/g, '')
  fileWrapper.url = url + (url.includes('?') ? '&' : '?') + '_cb=' + Date.now()
}

export function rotateImages (fileWrappers, right = true) {
  const ids = fileWrappers.map(fileWrapper => fileWrapper.id)
  const rotation = right ? 'RIGHT90' : 'LEFT90'

  return axiosPost({
    url: '/service/delta/document/rotate?rotation=' + rotation + '&id=' + ids,
    onSuccess: () => {
      getNotificationTarget().$q.notify({
        type: 'positive',
        message: 'Успешный поворот фотографий',
        progress: true,
        timeout: 3000
      })
    },
    onFailure: 'Ошибка поворота фотографии'
  }).finally(() => {
    // TODO await loadThumbnail
    fileWrappers.forEach(fileWrapper => {
      invalidateFileImageUrl(fileWrapper)
      fileWrapper.loadThumbnail(true)
    })
  })
}

export function loadObjectFiles ({ objectId, propertyKey, filter }) {
  if (objectId == null || objectId === -1) {
    return Promise.resolve([])
  }

  return axiosGet({
    url: '/service/delta/document/objectfiles?id=' + objectId + '&rel=%23' + propertyKey + (store.getters.getShowFilesHistoryParam ? '&history=true' : ''),
    onSuccess: (response) => {
      let documents = response.data.result
      if (filter) {
        documents = documents.filter(filter)
      }
      return documents
    },
    onFailure: 'Ошибка получения списка файлов объекта',
    throws: true
  })
}

export function loadPreloadedFiles (formId) {
  if (!formId) {
    return Promise.resolve([])
  }
  return axiosGet({
    url: '/service/delta/document/search?formId=' + formId,
    onSuccess: (response) => {
      const documents = response.data
      if (documents && documents.length) {
        for (const document of documents) {
          document._preloaded = true
        }
      }
      return documents
    },
    onFailure: 'Ошибка получения загруженных ранее фотографий',
    throws: true
  })
}

function getNotificationTarget () {
  const contentAreas = document.getElementsByClassName('content_display_area')
  return contentAreas[0].__vue__ || null
}

export function isImageDocument (document) {
  return document.contentType != null && document.contentType.startsWith('image/')
}

export function isVideoDocument (document) {
  return document.contentType != null && document.contentType.startsWith('video/')
}

export function isPdfDocument (file) {
  return !!file.contentType && (file.contentType === 'application/pdf' || (file.contentType === 'application/octet-stream' && file.name.includes('.pdf')))
}

export function isDocxDocument (file) {
  return !!file.contentType && file.contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
}

export function isDocDocument (file) {
  return !!file.contentType && file.contentType === 'application/msword'
}

export function isTxtDocument (file) {
  return !!file.contentType && file.contentType.includes('text/plain')
}
